import React, { useState } from 'react'
import { Box, Link, Typography } from '@mui/material'
import { IconChecks, IconCopy } from '@tabler/icons-react'
import { logEvent } from '../analytics/analytics'
import { CHATGPT_WEB_URL, RESACTLY_MCP_URL } from '../helpers/static-constants'
import { errorLog } from '../utils/log-helper'
import PlainModal from './modal/PlainModal'

interface ChatGptInstallModalProps {
  open: boolean
  handleClose: () => void
}

/**
 * Web-only instructions for adding Resactly to ChatGPT via developer mode while the official
 * plugin-store listing is under review. The MCP server intentionally uses No auth; these steps
 * must never gain credentials. ChatGPT shows its own "Elevated risk" notice when developer mode
 * is enabled — we mention it neutrally and never suggest it can be skipped or ignored.
 */
const INSTALL_STEPS: React.ReactNode[] = [
  <>
    Open{' '}
    <Link href={CHATGPT_WEB_URL} target="_blank" rel="noopener noreferrer">
      ChatGPT on the web
    </Link>
    .
  </>,
  <>
    Go to <strong>Settings → Plugins</strong>.
  </>,
  <>
    Scroll to the bottom and enable <strong>Developer mode</strong>.
  </>,
  <>
    ChatGPT shows an “Elevated risk” notice when developer mode is turned on —
    acknowledge it to continue.
  </>,
  <>
    Return to <strong>Plugins → Browse plugins</strong>.
  </>,
  <>
    Click the <strong>+</strong> next to “Search plugins”.
  </>,
  <>Enter the details below.</>,
  <>
    Select <strong>“I understand, I want to continue.”</strong>
  </>,
  <>
    Click <strong>Create</strong> — Resactly is now available in your chats.
  </>,
]

const ChatGptInstallModal: React.FC<ChatGptInstallModalProps> = ({
  open,
  handleClose,
}) => {
  const [isUrlCopied, setIsUrlCopied] = useState(false)

  const copyMcpUrl = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(RESACTLY_MCP_URL)
      setIsUrlCopied(true)
      logEvent('chatgpt_mcp_url_copied', { surface: 'install_modal' })
      setTimeout(() => setIsUrlCopied(false), 1500)
    } catch (error) {
      errorLog('Failed to copy MCP URL to clipboard', error)
    }
  }

  return (
    <PlainModal
      open={open}
      handleClose={handleClose}
      modelWidth="520px"
      title="Use Resactly in ChatGPT"
    >
      {/* The step list is tall; keep it scrollable so the modal fits short phone viewports. */}
      <Box sx={{ maxHeight: '65vh', overflowY: 'auto', pr: '4px' }}>
        <Typography variant="subtitle1" component="p" mb="12px">
          Our official ChatGPT listing is still under review, but you can add
          Resactly today as a developer plugin. This takes about a minute and
          currently requires ChatGPT on the web with developer mode enabled.
        </Typography>

        <Box component="ol" sx={{ pl: '20px', my: 0 }}>
          {INSTALL_STEPS.map((step, index) => (
            <Typography
              key={index}
              component="li"
              variant="subtitle1"
              sx={{ mb: '6px' }}
            >
              {step}
            </Typography>
          ))}
        </Box>

        <Box mt="12px">
          <Typography variant="subtitle1" mb="6px">
            Plugin details
          </Typography>
          <Box
            sx={{
              bgcolor: 'grey.100',
              p: 1.5,
              borderRadius: '8px',
              border: 1,
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="subtitle1">
              Name: <strong>Resactly</strong>
            </Typography>
            <Box
              role="button"
              aria-label="Copy MCP server URL"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                my: '4px',
              }}
              onClick={() => {
                void copyMcpUrl()
              }}
            >
              <Typography variant="subtitle1" sx={{ wordBreak: 'break-all' }}>
                MCP server: <strong>{RESACTLY_MCP_URL}</strong>
              </Typography>
              {isUrlCopied ? (
                <IconChecks size={16} stroke={2} data-testid="mcp-url-copied" />
              ) : (
                <IconCopy size={16} stroke={2} data-testid="mcp-url-copy" />
              )}
            </Box>
            {isUrlCopied && (
              <Typography variant="subtitle2" color="success.main">
                Copied to clipboard
              </Typography>
            )}
            <Typography variant="subtitle1">
              Authentication: <strong>No auth</strong>
            </Typography>
          </Box>
        </Box>
      </Box>
    </PlainModal>
  )
}

export default ChatGptInstallModal
